// Guide time geometry (EPG01, EPG02). Owner: WP04. Pure functions: instants are epoch ms (UTC); days are computed in the
// display timezone; the reversible display correction (preferences.guideDisplayOffsetMinutes) only shifts what is drawn.
import { dayKey } from '../../components/format.ts';

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Offset (ms) of `timeZone` from UTC at instant `t`. */
export function zoneOffset(t: number, timeZone: string): number {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
      .formatToParts(t).filter((x) => x.type !== 'literal').map((x) => [x.type, Number(x.value)]),
  ) as Record<string, number>;
  const asUtc = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!);
  return asUtc - Math.floor(t / 1000) * 1000;
}

/** Instant of local midnight for calendar day `key` (YYYY-MM-DD) in `timeZone` (DST-safe). */
export function midnight(key: string, timeZone: string): number {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d);
  let t = guess - zoneOffset(guess, timeZone);
  t = guess - zoneOffset(t, timeZone); // second pass settles DST edges
  return t;
}

export function addDays(key: string, n: number): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

export type GuideWindow = { day: string; start: number; end: number };

/**
 * Display window for a calendar day: local midnight → next local midnight, capped at 24 h because /api/guide serves at
 * most 24 h per request. ponytail: on a 25 h fall-back day the last hour shows on the next day's page instead.
 */
export function dayWindow(day: string, timeZone: string): GuideWindow {
  const start = midnight(day, timeZone);
  const end = Math.min(midnight(addDays(day, 1), timeZone), start + DAY);
  return { day, start, end };
}

/** Request interval for a display window: undo the display shift so data lines up with what's drawn. */
export function fetchRange(w: GuideWindow, shiftMin: number): { from: string; to: string } {
  return { from: new Date(w.start - shiftMin * MIN).toISOString(), to: new Date(w.end - shiftMin * MIN).toISOString() };
}

/** Selectable days: today plus days inside the provider's guide coverage, at most 7 back and 7 ahead (EPG03). */
export function coverageDays(now: number, timeZone: string, coverageFrom: string | null, coverageTo: string | null): string[] {
  const today = dayKey(now, timeZone);
  if (!coverageFrom || !coverageTo) return [today];
  const first = dayKey(Math.max(Date.parse(coverageFrom), now - 7 * DAY), timeZone);
  const last = dayKey(Math.min(Date.parse(coverageTo) - 1, now + 7 * DAY), timeZone);
  const days: string[] = [];
  for (let k = first < today ? first : today; k <= (last > today ? last : today) && days.length < 15; k = addDays(k, 1)) days.push(k);
  return days;
}

export type Placement = { left: number; width: number; clippedStart: boolean; clippedEnd: boolean };

/** Horizontal placement of a program: width is proportional to duration, clipped at the window edges. */
export function place(start: number, end: number, w: GuideWindow, pxPerMin: number, shiftMin = 0): Placement | null {
  const s = start + shiftMin * MIN;
  const e = end + shiftMin * MIN;
  if (!(e > s) || e <= w.start || s >= w.end) return null;
  const cs = Math.max(s, w.start);
  const ce = Math.min(e, w.end);
  return { left: ((cs - w.start) / MIN) * pxPerMin, width: ((ce - cs) / MIN) * pxPerMin, clippedStart: s < w.start, clippedEnd: e > w.end };
}

/** 30-minute slot boundaries from local midnight (so :30/:45 offset zones still line up with local half hours). */
export function slots(w: GuideWindow): number[] {
  const out: number[] = [];
  for (let t = w.start; t < w.end; t += 30 * MIN) out.push(t);
  return out;
}

export const minutes = (ms: number) => ms / MIN;

/** Now/Next request window: 6 h back to 18 h ahead, stable for an hour so the cache is reused (≤24 h per request). */
export function nowWindow(now: number): { from: string; to: string } {
  const from = Math.floor(now / HOUR) * HOUR - 6 * HOUR;
  return { from: new Date(from).toISOString(), to: new Date(from + 24 * HOUR).toISOString() };
}
